import { forwardRef, useImperativeHandle, useRef } from "react";
import { Turnstile } from "@marsidev/react-turnstile";

// Shared Cloudflare Turnstile widget. `action` MUST match the backend action
// (login | signup | waitlist | recover). Exposes reset() via ref so callers can
// request a fresh single-use token after any failed submit.
export const TurnstileGate = forwardRef(function TurnstileGate({ action, siteKey, onToken, className }, ref) {
  const innerRef = useRef(null);
  useImperativeHandle(ref, () => ({ reset: () => innerRef.current?.reset() }));
  if (!siteKey) return null;
  return (
    <div className={className} data-testid={`turnstile-${action}`}>
      <Turnstile
        ref={innerRef}
        siteKey={siteKey}
        options={{ theme: "dark", size: "flexible", action }}
        onSuccess={(t) => onToken(t)}
        onExpire={() => {
          onToken(null);
          innerRef.current?.reset();
        }}
        onError={() => onToken(null)}
      />
    </div>
  );
});

// Normalize an auth error into { isTurnstile, code, message }. Backend rejects bot checks
// with 403 { detail: { code: "turnstile_failed", message } }; other errors may carry a
// string detail, an { error } code, or a { message }.
export function turnstileError(err) {
  const data = err?.response?.data || {};
  const detail = data.detail;
  const code = (detail && typeof detail === "object" && detail.code) || data.code || data.error;
  const message =
    (detail && typeof detail === "object" && detail.message) ||
    (typeof detail === "string" ? detail : null) ||
    data.message;
  return { isTurnstile: code === "turnstile_failed", code, message };
}
